import { useCallback, useEffect, useRef, useState } from 'react';

export interface ChipAutocompleteProps {
  items: Array<{ value: string; label: string }>;
  onSelect: (value: string, label: string) => void;
  placeholder?: string;
  isLoading?: boolean;
  onSearch: (term: string) => void;
  minChars?: number;
}

interface OptionItemProps {
  item: { value: string; label: string };
  onSelect: (value: string, label: string) => void;
}

const OptionItem = ({ item, onSelect }: OptionItemProps) => {
  const handleClick = useCallback(() => {
    onSelect(item.value, item.label);
  }, [onSelect, item.value, item.label]);

  return (
    <button
      type="button"
      onClick={handleClick}
      className="py-1.5 w-full cursor-pointer border-none px-4 text-left text-sm text-text-primary hover:bg-chip-canvas-default-hover"
      style={{ backgroundColor: 'transparent' }}
    >
      {item.label}
    </button>
  );
};

export const ChipAutocomplete = ({
  items,
  onSelect,
  placeholder = '+ add',
  isLoading = false,
  onSearch,
  minChars = 2,
}: ChipAutocompleteProps) => {
  const [inputValue, setInputValue] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      setInputValue(newValue);
      const trimmed = newValue.trim();
      if (trimmed.length >= minChars) {
        onSearch(trimmed);
        setIsOpen(true);
      } else {
        setIsOpen(false);
      }
    },
    [onSearch, minChars],
  );

  const handleSelect = useCallback(
    (value: string, label: string) => {
      onSelect(value, label);
      setInputValue('');
      setIsOpen(false);
      inputRef.current?.focus();
    },
    [onSelect],
  );

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      setInputValue('');
      setIsOpen(false);
    }
  }, []);

  const handleFocus = useCallback(() => {
    if (inputValue.trim().length >= minChars && items.length > 0) {
      setIsOpen(true);
    }
  }, [inputValue, minChars, items.length]);

  const showDropdown = isOpen && (items.length > 0 || (isLoading && inputValue.trim().length >= minChars));

  return (
    <div ref={containerRef} className="relative inline-flex">
      <input
        ref={inputRef}
        type="text"
        value={inputValue}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onFocus={handleFocus}
        className="py-0.5 focus:border-interactive-primary rounded-full border-2 border-chip-border-default-idle bg-chip-canvas-default-idle px-4 text-sm leading-tight outline-none"
        style={{ width: '120px' }}
        placeholder={placeholder}
      />
      {showDropdown && (
        <div
          className="mt-1 absolute top-full left-0 z-50 min-w-[200px]"
          style={{
            backgroundColor: 'var(--chip-canvas-default-idle)',
            border: '2px solid var(--chip-border-default-idle)',
            borderRadius: '20px',
            padding: '4px 0',
          }}
        >
          {isLoading ? (
            <span className="py-1.5 block px-4 text-sm text-text-tertiary">Loading...</span>
          ) : (
            items.map((item) => <OptionItem key={item.value} item={item} onSelect={handleSelect} />)
          )}
        </div>
      )}
    </div>
  );
};
